import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabaseClient";
import { createNotification } from "@/lib/createNotification";
import type { Job } from "@/pages/admin/jobs/components/JobModal";

/* ─────────────────────────────── Types ─────────────────────────────── */
type JobStep = {
  id: string;
  tenant_id: string;
  job_id: string;
  step_key: string;
  title: string;
  description: string | null;
  status: string;
  sort_order: number;
  due_date: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
};

type JobTask = {
  id: string;
  tenant_id: string;
  job_id: string;
  title: string;
  status: string;
  due_date: string | null;
  assigned_to: string | null;
  priority: string;
  notes: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
};

/* ─────────────────────────────── Config ─────────────────────────────── */
const STEP_STATUSES = [
  { value: "pending",     label: "Pending" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed",   label: "Completed" },
  { value: "blocked",     label: "Blocked" },
];

const STEP_STATUS_STYLES: Record<string, string> = {
  pending:     "bg-slate-100 text-slate-500",
  in_progress: "bg-amber-100 text-amber-600",
  completed:   "bg-emerald-100 text-emerald-600",
  blocked:     "bg-red-100 text-red-500",
};

const TASK_STATUS_CFG: Record<string, { label: string; icon: string; color: string; bg: string; border: string }> = {
  pending:     { label: "Pending",     icon: "ri-time-line",             color: "text-slate-500",   bg: "bg-slate-100",   border: "border-slate-200" },
  in_progress: { label: "In Progress", icon: "ri-loader-3-line",         color: "text-amber-600",   bg: "bg-amber-50",    border: "border-amber-200" },
  done:        { label: "Done",        icon: "ri-checkbox-circle-fill",  color: "text-emerald-600", bg: "bg-emerald-50",  border: "border-emerald-200" },
  cancelled:   { label: "Cancelled",   icon: "ri-close-circle-line",     color: "text-slate-400",   bg: "bg-slate-100",   border: "border-slate-200" },
};

const PRIORITY_CFG: Record<string, { label: string; color: string; dot: string }> = {
  low:    { label: "Low",    color: "text-slate-400", dot: "bg-slate-300" },
  normal: { label: "Normal", color: "text-slate-500", dot: "bg-slate-400" },
  high:   { label: "High",   color: "text-amber-600", dot: "bg-amber-400" },
  urgent: { label: "Urgent", color: "text-red-600",   dot: "bg-red-500" },
};

function fmtDate(d: string | null) {
  if (!d) return null;
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function isOverdue(d: string | null, status: string) {
  if (!d || status === "done" || status === "completed" || status === "cancelled") return false;
  return new Date(d) < new Date();
}

/* ═══════════════════════════════════════════════════════════════════════ */

interface Props {
  job: Job;
  tenantId: string;
}

type TaskView = "all" | "pending" | "in_progress" | "done";
type TaskForm = { title: string; priority: string; due_date: string; assigned_to: string; notes: string };
const EMPTY_TASK: TaskForm = { title: "", priority: "normal", due_date: "", assigned_to: "", notes: "" };

export default function WorkflowTab({ job, tenantId }: Props) {
  /* ── Steps state ── */
  const [steps, setSteps] = useState<JobStep[]>([]);
  const [stepsLoading, setStepsLoading] = useState(true);
  const [savingStepId, setSavingStepId] = useState<string | null>(null);
  const [editingDueDate, setEditingDueDate] = useState<string | null>(null);

  /* ── Tasks state ── */
  const [tasks, setTasks] = useState<JobTask[]>([]);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [editingTask, setEditingTask] = useState<JobTask | null>(null);
  const [taskForm, setTaskForm] = useState<TaskForm>({ ...EMPTY_TASK });
  const [savingTask, setSavingTask] = useState(false);
  const [savingTaskId, setSavingTaskId] = useState<string | null>(null);
  const [taskView, setTaskView] = useState<TaskView>("all");
  const [expandedTaskId, setExpandedTaskId] = useState<string | null>(null);

  /* ── Fetch ── */
  const fetchSteps = useCallback(async () => {
    setStepsLoading(true);
    const { data } = await supabase
      .from("job_steps")
      .select("*")
      .eq("job_id", job.id)
      .eq("tenant_id", tenantId)
      .order("sort_order", { ascending: true });
    setSteps((data ?? []) as JobStep[]);
    setStepsLoading(false);
  }, [job.id, tenantId]);

  const fetchTasks = useCallback(async () => {
    setTasksLoading(true);
    const { data } = await supabase
      .from("job_tasks")
      .select("*")
      .eq("job_id", job.id)
      .eq("tenant_id", tenantId)
      .order("created_at", { ascending: true });
    setTasks((data ?? []) as JobTask[]);
    setTasksLoading(false);
  }, [job.id, tenantId]);

  useEffect(() => {
    fetchSteps();
    fetchTasks();
  }, [fetchSteps, fetchTasks]);

  /* ── Steps handlers ── */
  const handleStepStatusChange = async (step: JobStep, newStatus: string) => {
    setSavingStepId(step.id);
    const completedAt = newStatus === "completed" ? new Date().toISOString() : null;
    const { data, error } = await supabase
      .from("job_steps")
      .update({ status: newStatus, completed_at: completedAt, updated_at: new Date().toISOString() })
      .eq("id", step.id)
      .eq("tenant_id", tenantId)
      .select()
      .maybeSingle();
    if (!error && data) {
      setSteps((prev) => prev.map((s) => s.id === step.id ? (data as JobStep) : s));
      if (newStatus === "completed") {
        createNotification({ tenantId, type: "success", title: "Step completed", message: `"${step.title}" marked as completed.` });
      }
    }
    setSavingStepId(null);
  };

  const handleDueDateSave = async (step: JobStep, newDate: string) => {
    setSavingStepId(step.id);
    const { data } = await supabase
      .from("job_steps")
      .update({ due_date: newDate || null, updated_at: new Date().toISOString() })
      .eq("id", step.id)
      .eq("tenant_id", tenantId)
      .select()
      .maybeSingle();
    if (data) setSteps((prev) => prev.map((s) => s.id === step.id ? (data as JobStep) : s));
    setSavingStepId(null);
    setEditingDueDate(null);
  };

  /* ── Task handlers ── */
  const openNewTask = () => {
    setEditingTask(null);
    setTaskForm({ ...EMPTY_TASK });
    setShowTaskForm(true);
  };

  const openEditTask = (t: JobTask) => {
    setEditingTask(t);
    setTaskForm({
      title: t.title,
      priority: t.priority,
      due_date: t.due_date ? t.due_date.slice(0, 16) : "",
      assigned_to: t.assigned_to ?? "",
      notes: t.notes ?? "",
    });
    setShowTaskForm(true);
    setExpandedTaskId(null);
  };

  const cancelTaskForm = () => { setShowTaskForm(false); setEditingTask(null); };

  const handleSaveTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!taskForm.title.trim()) return;
    setSavingTask(true);
    const payload = {
      title: taskForm.title.trim(),
      priority: taskForm.priority,
      due_date: taskForm.due_date || null,
      assigned_to: taskForm.assigned_to.trim() || null,
      notes: taskForm.notes.trim() || null,
    };
    if (editingTask) {
      const { data } = await supabase
        .from("job_tasks")
        .update({ ...payload, updated_at: new Date().toISOString() })
        .eq("id", editingTask.id)
        .eq("tenant_id", tenantId)
        .select()
        .maybeSingle();
      if (data) setTasks((prev) => prev.map((t) => t.id === editingTask.id ? (data as JobTask) : t));
    } else {
      const { data } = await supabase
        .from("job_tasks")
        .insert({ ...payload, tenant_id: tenantId, job_id: job.id, status: "pending" })
        .select()
        .maybeSingle();
      if (data) setTasks((prev) => [...prev, data as JobTask]);
    }
    setSavingTask(false);
    cancelTaskForm();
  };

  const handleTaskStatusChange = async (task: JobTask, newStatus: string) => {
    setSavingTaskId(task.id);
    const completedAt = newStatus === "done" ? new Date().toISOString() : null;
    const { data } = await supabase
      .from("job_tasks")
      .update({ status: newStatus, completed_at: completedAt, updated_at: new Date().toISOString() })
      .eq("id", task.id)
      .eq("tenant_id", tenantId)
      .select()
      .maybeSingle();
    if (data) {
      setTasks((prev) => prev.map((t) => t.id === task.id ? (data as JobTask) : t));
      if (newStatus === "done") {
        createNotification({ tenantId, type: "success", title: "Task done", message: `"${task.title}" marked as done.` });
      }
    }
    setSavingTaskId(null);
  };

  const handleQuickDone = async (task: JobTask) => {
    const isDone = task.status === "done";
    await handleTaskStatusChange(task, isDone ? "pending" : "done");
  };

  const handleDeleteTask = async (task: JobTask) => {
    setSavingTaskId(task.id);
    await supabase.from("job_tasks").delete().eq("id", task.id).eq("tenant_id", tenantId);
    setTasks((prev) => prev.filter((t) => t.id !== task.id));
    setSavingTaskId(null);
  };

  /* ── Derived ── */
  const stepsCompleted = steps.filter((s) => s.status === "completed").length;
  const stepsPct = steps.length > 0 ? Math.round((stepsCompleted / steps.length) * 100) : 0;

  const tasksDone = tasks.filter((t) => t.status === "done").length;
  const tasksPending = tasks.filter((t) => t.status === "pending").length;
  const tasksInProgress = tasks.filter((t) => t.status === "in_progress").length;

  const filteredTasks = tasks.filter((t) => {
    if (taskView === "all") return t.status !== "cancelled";
    return t.status === taskView;
  });

  /* ══════════════════════════════ RENDER ═══════════════════════════════ */
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

      {/* ══ LEFT: Production Steps ══ */}
      <div className="space-y-4">

        {/* Steps header */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[#0f172a] font-semibold text-sm flex items-center gap-2">
              <i className="ri-git-branch-line text-[#C8A96E]"></i>
              Production Steps
            </h3>
            <span className="text-[#C8A96E] text-sm font-semibold">{stepsCompleted}/{steps.length} · {stepsPct}%</span>
          </div>
          <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{ width: `${stepsPct}%`, background: stepsPct === 100 ? "#10b981" : "#C8A96E" }}
            />
          </div>
          {stepsPct === 100 && (
            <p className="text-emerald-600 text-xs mt-2 flex items-center gap-1.5">
              <i className="ri-checkbox-circle-fill"></i>
              All steps completed — job ready to close!
            </p>
          )}
        </div>

        {/* Steps list */}
        {stepsLoading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => <div key={i} className="h-16 bg-slate-100 rounded-xl animate-pulse" />)}
          </div>
        ) : steps.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-12 bg-white rounded-xl border border-slate-200">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-slate-100">
              <i className="ri-git-branch-line text-slate-300 text-xl"></i>
            </div>
            <p className="text-slate-400 text-sm">No workflow steps found.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {steps.map((step, idx) => {
              const isSaving = savingStepId === step.id;
              const isEditingDate = editingDueDate === step.id;
              const overdue = isOverdue(step.due_date, step.status);

              return (
                <div
                  key={step.id}
                  className={`rounded-xl border transition-all ${
                    step.status === "completed" ? "border-emerald-200 bg-emerald-50/40" :
                    step.status === "blocked" ? "border-red-200 bg-red-50/40" :
                    overdue ? "border-amber-200 bg-amber-50/40" :
                    "border-slate-200 bg-white"
                  }`}
                >
                  <div className="p-4">
                    <div className="flex items-start gap-3">
                      {/* Step number / check */}
                      <button
                        onClick={() => handleStepStatusChange(step, step.status === "completed" ? "pending" : "completed")}
                        disabled={isSaving}
                        className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-semibold flex-shrink-0 mt-0.5 transition-all cursor-pointer ${STEP_STATUS_STYLES[step.status]} hover:ring-2 hover:ring-offset-1 hover:ring-[#C8A96E]/40`}
                      >
                        {isSaving
                          ? <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                          : step.status === "completed"
                            ? <i className="ri-check-line"></i>
                            : <span>{idx + 1}</span>
                        }
                      </button>

                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium mb-2 ${step.status === "completed" ? "text-slate-300 line-through" : "text-[#0f172a]"}`}>
                          {step.title}
                        </p>
                        <div className="flex items-center gap-2 flex-wrap">
                          {/* Status pill */}
                          <select
                            value={step.status}
                            onChange={(e) => handleStepStatusChange(step, e.target.value)}
                            disabled={isSaving}
                            className={`text-xs font-medium px-2.5 py-1 rounded-full border-0 outline-none cursor-pointer appearance-none ${STEP_STATUS_STYLES[step.status]} disabled:opacity-50`}
                          >
                            {STEP_STATUSES.map((s) => (
                              <option key={s.value} value={s.value}>{s.label}</option>
                            ))}
                          </select>

                          {/* Due date */}
                          {isEditingDate ? (
                            <input
                              type="datetime-local"
                              defaultValue={step.due_date ? step.due_date.slice(0, 16) : ""}
                              autoFocus
                              className="bg-white border border-slate-200 rounded-lg px-2 py-1 text-xs text-[#0f172a] outline-none focus:border-[#C8A96E]/50"
                              onBlur={(e) => handleDueDateSave(step, e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") handleDueDateSave(step, (e.target as HTMLInputElement).value);
                                if (e.key === "Escape") setEditingDueDate(null);
                              }}
                            />
                          ) : (
                            <button
                              onClick={() => setEditingDueDate(step.id)}
                              className={`flex items-center gap-1 text-xs cursor-pointer whitespace-nowrap transition-colors ${
                                overdue ? "text-amber-600 hover:text-amber-700" : "text-slate-400 hover:text-slate-600"
                              }`}
                            >
                              <i className="ri-calendar-2-line text-[10px]"></i>
                              {step.due_date ? fmtDate(step.due_date) : "Set date"}
                              {overdue && <span className="font-medium"> · Overdue</span>}
                            </button>
                          )}

                          {step.completed_at && (
                            <span className="text-[10px] text-emerald-500 whitespace-nowrap">
                              ✓ {fmtDate(step.completed_at)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ══ RIGHT: Internal Tasks ══ */}
      <div className="space-y-4">

        {/* Tasks header */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[#0f172a] font-semibold text-sm flex items-center gap-2">
              <i className="ri-task-line text-[#C8A96E]"></i>
              Internal Tasks
            </h3>
            {!showTaskForm && (
              <button
                onClick={openNewTask}
                className="flex items-center gap-1.5 bg-[#C8A96E] hover:bg-[#b8965a] text-black text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line text-xs"></i>
                Add Task
              </button>
            )}
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-2 text-center">
            <button
              onClick={() => setTaskView(taskView === "pending" ? "all" : "pending")}
              className={`p-2 rounded-lg border transition-all cursor-pointer ${taskView === "pending" ? "bg-slate-100 border-slate-300" : "bg-slate-50 border-slate-100 hover:border-slate-200"}`}
            >
              <p className="text-[#0f172a] font-bold text-base">{tasksPending}</p>
              <p className="text-slate-400 text-[10px]">Pending</p>
            </button>
            <button
              onClick={() => setTaskView(taskView === "in_progress" ? "all" : "in_progress")}
              className={`p-2 rounded-lg border transition-all cursor-pointer ${taskView === "in_progress" ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-100 hover:border-slate-200"}`}
            >
              <p className="text-amber-600 font-bold text-base">{tasksInProgress}</p>
              <p className="text-slate-400 text-[10px]">In Progress</p>
            </button>
            <button
              onClick={() => setTaskView(taskView === "done" ? "all" : "done")}
              className={`p-2 rounded-lg border transition-all cursor-pointer ${taskView === "done" ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-100 hover:border-slate-200"}`}
            >
              <p className="text-emerald-600 font-bold text-base">{tasksDone}</p>
              <p className="text-slate-400 text-[10px]">Done</p>
            </button>
          </div>

          {/* Progress bar */}
          {tasks.length > 0 && (() => {
            const activeTasks = tasks.filter(t => t.status !== "cancelled");
            const pct = activeTasks.length > 0 ? Math.round((tasksDone / activeTasks.length) * 100) : 0;
            const allDone = activeTasks.length > 0 && tasksDone === activeTasks.length;
            return (
              <div className="mt-3">
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${pct}%`,
                      background: allDone ? "#10b981" : "#C8A96E",
                    }}
                  />
                </div>
              </div>
            );
          })()}
        </div>

        {/* Task form */}
        {showTaskForm && (
          <form onSubmit={handleSaveTask} className="bg-white rounded-xl border border-[#C8A96E]/30 p-5 space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-[#0f172a] font-semibold text-sm flex items-center gap-2">
                <i className="ri-task-line text-[#C8A96E]"></i>
                {editingTask ? "Edit Task" : "New Internal Task"}
              </p>
              <button type="button" onClick={cancelTaskForm} className="text-slate-300 hover:text-slate-500 cursor-pointer">
                <span className="w-5 h-5 flex items-center justify-center"><i className="ri-close-line"></i></span>
              </button>
            </div>

            <div>
              <label className="text-slate-400 text-xs mb-1.5 block uppercase tracking-wider">Task *</label>
              <input
                type="text" value={taskForm.title}
                onChange={(e) => setTaskForm((p) => ({ ...p, title: e.target.value }))}
                required placeholder="Edit wedding highlight reel..."
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors"
                autoFocus
              />
            </div>

            {/* Priority pills */}
            <div>
              <label className="text-slate-400 text-xs mb-2 block uppercase tracking-wider">Priority</label>
              <div className="flex items-center gap-2 flex-wrap">
                {Object.entries(PRIORITY_CFG).map(([val, cfg]) => (
                  <button
                    key={val} type="button"
                    onClick={() => setTaskForm((p) => ({ ...p, priority: val }))}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all cursor-pointer whitespace-nowrap ${
                      taskForm.priority === val
                        ? "bg-[#C8A96E]/10 border-[#C8A96E]/40 text-[#a6853e]"
                        : "bg-white border-slate-200 text-slate-400 hover:border-slate-300"
                    }`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot} flex-shrink-0`}></span>
                    {cfg.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-slate-400 text-xs mb-1.5 block uppercase tracking-wider">Due Date</label>
                <input
                  type="datetime-local" value={taskForm.due_date}
                  onChange={(e) => setTaskForm((p) => ({ ...p, due_date: e.target.value }))}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/50 cursor-pointer"
                />
              </div>
              <div>
                <label className="text-slate-400 text-xs mb-1.5 block uppercase tracking-wider">Assigned To</label>
                <input
                  type="text" value={taskForm.assigned_to}
                  onChange={(e) => setTaskForm((p) => ({ ...p, assigned_to: e.target.value }))}
                  placeholder="Name or @user"
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50"
                />
              </div>
            </div>

            <div>
              <label className="text-slate-400 text-xs mb-1.5 block uppercase tracking-wider">Notes</label>
              <input
                type="text" value={taskForm.notes}
                onChange={(e) => setTaskForm((p) => ({ ...p, notes: e.target.value }))}
                placeholder="Additional context..."
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50"
              />
            </div>

            <div className="flex justify-end gap-2">
              <button type="button" onClick={cancelTaskForm} className="px-4 py-2 text-sm text-slate-500 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer whitespace-nowrap">
                Cancel
              </button>
              <button type="submit" disabled={savingTask} className="flex items-center gap-1.5 px-5 py-2 bg-[#C8A96E] hover:bg-[#b8965a] text-black text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50">
                {savingTask
                  ? <><i className="ri-loader-4-line animate-spin text-sm"></i> Saving…</>
                  : editingTask ? "Update Task" : "Add Task"
                }
              </button>
            </div>
          </form>
        )}

        {/* Tasks list */}
        {tasksLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => <div key={i} className="h-14 bg-slate-100 rounded-xl animate-pulse" />)}
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-12 bg-white rounded-xl border border-slate-200 text-center">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-slate-100">
              <i className="ri-task-line text-slate-300 text-xl"></i>
            </div>
            <p className="text-slate-500 text-sm font-medium">
              {taskView === "all" ? "No tasks yet" : `No ${taskView.replace("_", " ")} tasks`}
            </p>
            {taskView === "all" && (
              <p className="text-slate-400 text-xs max-w-xs">
                Add internal tasks for the team — editing, culling, color grading, delivery prep...
              </p>
            )}
            {taskView === "all" && (
              <button
                onClick={openNewTask}
                className="text-xs text-[#C8A96E] hover:text-[#a6853e] cursor-pointer flex items-center gap-1 transition-colors"
              >
                <i className="ri-add-line text-xs"></i>
                Create first task
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {filteredTasks.map((task) => {
              const cfg = TASK_STATUS_CFG[task.status] ?? TASK_STATUS_CFG.pending;
              const priCfg = PRIORITY_CFG[task.priority] ?? PRIORITY_CFG.normal;
              const isBusy = savingTaskId === task.id;
              const overdue = isOverdue(task.due_date, task.status);
              const isExpanded = expandedTaskId === task.id;
              const isDone = task.status === "done";

              return (
                <div
                  key={task.id}
                  className={`bg-white rounded-xl border transition-all group ${
                    isDone ? "border-emerald-200 opacity-70" :
                    overdue ? "border-amber-200" :
                    task.status === "in_progress" ? "border-amber-100" :
                    "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="flex items-center gap-3 p-3.5">
                    {/* Quick done checkbox */}
                    <button
                      onClick={() => handleQuickDone(task)}
                      disabled={isBusy}
                      className={`w-6 h-6 flex items-center justify-center rounded-full border-2 flex-shrink-0 transition-all cursor-pointer ${
                        isDone
                          ? "border-emerald-400 bg-emerald-400 text-white"
                          : "border-slate-300 hover:border-[#C8A96E] hover:bg-[#C8A96E]/10 text-transparent hover:text-[#C8A96E]"
                      }`}
                      title={isDone ? "Mark pending" : "Mark done"}
                    >
                      {isBusy
                        ? <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        : <i className="ri-check-line text-[10px]"></i>
                      }
                    </button>

                    {/* Priority dot */}
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${priCfg.dot}`} title={`${priCfg.label} priority`}></span>

                    {/* Title + meta */}
                    <div className="flex-1 min-w-0">
                      <button
                        onClick={() => setExpandedTaskId(isExpanded ? null : task.id)}
                        className="text-left w-full cursor-pointer"
                      >
                        <p className={`text-sm font-medium truncate ${isDone ? "line-through text-slate-300" : "text-[#0f172a]"}`}>
                          {task.title}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          {task.due_date && (
                            <span className={`text-[10px] flex items-center gap-0.5 ${overdue ? "text-amber-600 font-medium" : "text-slate-400"}`}>
                              <i className="ri-calendar-2-line text-[10px]"></i>
                              {fmtDate(task.due_date)}
                              {overdue && " · Overdue"}
                            </span>
                          )}
                          {task.assigned_to && (
                            <span className="text-[10px] text-slate-400 flex items-center gap-0.5">
                              <i className="ri-user-line text-[10px]"></i>
                              {task.assigned_to}
                            </span>
                          )}
                        </div>
                      </button>
                    </div>

                    {/* Status badge */}
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium border whitespace-nowrap flex items-center gap-1 flex-shrink-0 ${cfg.bg} ${cfg.color} ${cfg.border}`}>
                      <i className={`${cfg.icon} text-[10px]`}></i>
                      <span className="hidden sm:inline">{cfg.label}</span>
                    </span>

                    {/* Actions */}
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                      <button
                        onClick={() => openEditTask(task)}
                        className="w-6 h-6 flex items-center justify-center rounded-lg text-slate-300 hover:text-slate-600 hover:bg-slate-100 transition-all cursor-pointer"
                        title="Edit"
                      >
                        <i className="ri-pencil-line text-[10px]"></i>
                      </button>
                      <button
                        onClick={() => handleDeleteTask(task)}
                        disabled={isBusy}
                        className="w-6 h-6 flex items-center justify-center rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-all cursor-pointer"
                        title="Delete"
                      >
                        <i className="ri-delete-bin-line text-[10px]"></i>
                      </button>
                    </div>
                  </div>

                  {/* Expanded: status change + notes */}
                  {isExpanded && (
                    <div className="border-t border-slate-100 px-4 py-3 bg-slate-50/50 space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-slate-400 text-xs">Status:</p>
                        {Object.entries(TASK_STATUS_CFG).map(([val, c]) => (
                          <button
                            key={val}
                            onClick={() => handleTaskStatusChange(task, val)}
                            disabled={isBusy || task.status === val}
                            className={`flex items-center gap-1.5 text-[10px] font-medium px-2.5 py-1 rounded-full border transition-all cursor-pointer whitespace-nowrap disabled:cursor-not-allowed ${
                              task.status === val
                                ? `${c.bg} ${c.color} ${c.border} ring-1 ring-offset-1 ring-[#C8A96E]/30`
                                : `bg-white border-slate-200 text-slate-400 hover:${c.bg} hover:${c.color}`
                            }`}
                          >
                            <i className={`${c.icon} text-[10px]`}></i>
                            {c.label}
                          </button>
                        ))}
                      </div>
                      {task.notes && (
                        <p className="text-slate-500 text-xs bg-white border border-slate-100 rounded-lg p-2.5 leading-relaxed">
                          {task.notes}
                        </p>
                      )}
                      {task.completed_at && (
                        <p className="text-emerald-500 text-[10px] flex items-center gap-1">
                          <i className="ri-check-double-line"></i>
                          Completed {fmtDate(task.completed_at)}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Cancelled tasks collapsed */}
            {tasks.filter(t => t.status === "cancelled").length > 0 && taskView === "all" && (
              <details className="group">
                <summary className="text-xs text-slate-400 cursor-pointer hover:text-slate-500 flex items-center gap-1.5 px-1 py-1 transition-colors list-none">
                  <i className="ri-arrow-right-s-line group-open:rotate-90 transition-transform text-slate-300"></i>
                  {tasks.filter(t => t.status === "cancelled").length} cancelled task(s)
                </summary>
                <div className="space-y-2 mt-2 opacity-50">
                  {tasks.filter(t => t.status === "cancelled").map(task => (
                    <div key={task.id} className="bg-white rounded-xl border border-slate-200 px-4 py-3 flex items-center gap-3">
                      <div className="w-6 h-6 flex items-center justify-center rounded-full border-2 border-slate-200 flex-shrink-0">
                        <i className="ri-close-line text-[10px] text-slate-300"></i>
                      </div>
                      <p className="text-slate-400 text-sm line-through flex-1 truncate">{task.title}</p>
                      <button
                        onClick={() => handleDeleteTask(task)}
                        className="text-slate-300 hover:text-red-400 cursor-pointer transition-colors"
                      >
                        <i className="ri-delete-bin-line text-xs"></i>
                      </button>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}
      </div>
    </div>
  );
}